import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Menu,
  Moon,
  Sun,
  LogOut,
  LayoutDashboard,
  Building2,
  Package,
  Ticket,
  Smartphone,
  CreditCard,
  TrendingUp,
  Banknote,
  Store,
  Settings,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import LanguageSwitcher from '../components/common/LanguageSwitcher';
import Brand from '../components/common/Brand';
import NotificationButton from '../components/NotificationButton';
import merchantService from '../services/merchantService';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const MERCHANT_ICONS = {
  '/': LayoutDashboard,
  '/branches': Building2,
  '/products': Package,
  '/offers': Ticket,
  '/devices': Smartphone,
  '/payment-sessions': CreditCard,
  '/sales': TrendingUp,
  '/payments': Banknote,
  '/profile': Store,
  '/settings': Settings,
};

const MerchantLayout = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [merchantProfile, setMerchantProfile] = useState(null);
  const { user, userProfile, logout, session } = useAuth();
  const { t, isRTL, direction } = useLanguage();

  const merchantLinks = [
    { to: '/', label: t('dashboard') },
    { to: '/branches', label: 'Branches' },
    { to: '/products', label: t('myProducts') },
    { to: '/offers', label: t('myOffers') },
    { to: '/devices', label: t('myDevices') },
    { to: '/payment-sessions', label: t('paymentSessions') },
    { to: '/sales', label: t('sales') },
    { to: '/payments', label: t('payments') },
    { to: '/profile', label: t('profile') },
    { to: '/settings', label: t('settings') },
  ];

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    const fetchMerchantProfile = async () => {
      try {
        const token = userProfile?.token || session?.access_token;
        if (!token || !userProfile?.isMerchant) return;

        merchantService.setAuthToken(token);
        const response = await merchantService.getProfile();

        if (response.success && response.data) {
          setMerchantProfile(response.data);
        }
      } catch (error) {
        console.error('Error fetching merchant profile:', error);
      }
    };

    if (userProfile?.isMerchant) {
      fetchMerchantProfile();
    }
  }, [userProfile?.token, session?.access_token, userProfile?.isMerchant]);

  const closeSidebar = () => setSidebarOpen(false);

  const storeName =
    merchantProfile?.business_name ||
    userProfile?.business_name ||
    userProfile?.name ||
    t('myStore');

  const renderNavLinks = () => (
    <nav className="flex flex-col gap-1 px-2 py-2">
      {merchantLinks.map((link) => {
        const Icon = MERCHANT_ICONS[link.to] || LayoutDashboard;
        return (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/'}
            onClick={closeSidebar}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-start text-sm font-medium transition-colors',
                'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                isActive && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
              )
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{link.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  const sidebarHeader = (
    <div className="flex flex-col items-start gap-2 px-4 py-4 text-start">
      <Brand size="md" />
      <Badge variant="secondary" className="w-fit">
        {t('merchantPortal')}
      </Badge>
      {userProfile && (
        <p className="w-full truncate text-sm font-semibold text-foreground">{storeName}</p>
      )}
    </div>
  );

  return (
    <div
      dir={direction}
      className={cn(
        'flex min-h-screen bg-background text-foreground transition-colors duration-300',
        isRTL ? 'rtl' : 'ltr'
      )}
    >
      <aside className="hidden w-64 shrink-0 flex-col border-s border-border bg-card md:flex">
        {sidebarHeader}
        <Separator />
        <div className="flex-1 overflow-y-auto">{renderNavLinks()}</div>
      </aside>

      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent
          side={isRTL ? 'right' : 'left'}
          className="flex w-72 flex-col gap-0 p-0"
          dir={direction}
        >
          <SheetHeader className="space-y-0 border-b border-border p-0 text-start">
            <SheetTitle className="sr-only">{t('merchantPortal')}</SheetTitle>
            {sidebarHeader}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto" dir={direction}>
            {renderNavLinks()}
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-2 border-b border-border bg-card px-2 py-3 sm:px-4 md:px-6 md:py-4">
          <div className="flex min-w-0 items-center gap-2 md:gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Toggle menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            <div className="truncate text-base font-semibold text-foreground sm:text-lg md:text-xl">
              {t('merchantDashboard')}
            </div>

            {userProfile && (
              <span className="hidden max-w-[200px] truncate text-sm text-muted-foreground lg:inline">
                {userProfile.name || userProfile.business_name || user?.email}
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1 md:gap-2">
            <NotificationButton />
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? t('lightMode') : t('darkMode')}
              aria-label={darkMode ? t('lightMode') : t('darkMode')}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="destructive" size="sm" onClick={logout} className="gap-1.5">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('logout')}</span>
            </Button>
          </div>
        </header>

        <main className="w-full max-w-full flex-1 overflow-x-hidden bg-background p-2 text-foreground transition-colors duration-300 sm:p-4 md:p-6">
          <div className="max-w-full overflow-x-hidden">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default MerchantLayout;
